import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  styled,
  Typography,
} from '@worthy-npm/worthy-common-ui-components';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import calendar from 'dayjs/plugin/calendar';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import ScrollShadowContainer from '../common/ScrollShadowContainer';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  Day,
  getPickupAvailability,
  getScheduleShippingLoading,
  getSlot,
  getUserLocalTime,
  PickupAvailability,
  scheduleShipping,
  setSlot,
  setShippingMethod,
  Slot,
} from '../../slices/schedulingSlice';
import { SchedulingType } from '../../types/scheduling';
import WorthyAPI from '../../services/worthyAPI';
import { selectItemPublicId } from '../../slices/submitSlice';
import ErrorState from './ErrorState';
import NextButton from './NextButton';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(calendar);
dayjs.extend(isSameOrAfter);

const StyledChip = styled(Chip)<{ selected: boolean }>(({ theme, selected }) => ({
  backgroundColor: theme.palette.background.default,
  color: selected ? theme.palette.highlight.main : theme.palette.text.primary,
  minWidth: 100,
  width: 'fit-content',
  '& .MuiChip-label': {
    paddingTop: 6,
    paddingBottom: 6,
  },
}));

const StyledPaper = styled(Paper)<{ selected: boolean }>(({ theme, selected }) => ({
  backgroundColor: theme.palette.background.default,
  color: selected ? theme.palette.highlight.main : theme.palette.text.primary,
  width: '100%',
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  cursor: 'pointer',
  outline: selected ? `1px solid ${theme.palette.highlight.main}` : 'none',
}));

const roundTimeWithDayjs = (time: dayjs.Dayjs, interval: number) => {
  const min = time.minute();
  const remainder = min % interval;
  return remainder === 0
    ? time.second(0).millisecond(0)
    : time
      .add(interval - remainder, 'minutes')
      .second(0)
      .millisecond(0);
};

const buildSlots = (
  pickupAvailability: PickupAvailability[],
  localTime: string,
  ignoreLocalTime = false,
): Day[] => {
  if (!pickupAvailability?.length || !localTime) {
    console.error(
      `Missing required field pickupAvailability.length: ${pickupAvailability.length} or localTime: ${localTime}`,
    );
    return [];
  }

  // todo: use actual client time
  const clientLocalTime = (!ignoreLocalTime && dayjs().tz('America/New_York').format()) || localTime;

  const PICKUP_START_TIME = 11;
  const MIN_PICKUP_START_TIME_THRESHOLD = 9;
  const FEDEX_OFFICIAL_START_TIME = 13;
  const MAX_SCHEDULE_PICKUP_HOURS = 48;
  const maxSchedulePickupTime = dayjs(clientLocalTime).add(MAX_SCHEDULE_PICKUP_HOURS, 'hours');
  const userLocalTime = dayjs(clientLocalTime);

  const groupedSlotsByDay: Day[] = [];

  pickupAvailability.forEach((pickup) => {
    const [cutOffHour, cutOffMinute] = pickup.cut_off_time.split(':').map(Number);
    const cutOffTime = cutOffHour;
    const leftMinutes = cutOffMinute;
    const accessTime = pickup.access_time;
    const { date } = pickup;

    const startTime = cutOffTime <= FEDEX_OFFICIAL_START_TIME ? MIN_PICKUP_START_TIME_THRESHOLD : PICKUP_START_TIME;

    const time = dayjs(date).isSame(userLocalTime, 'day') && userLocalTime.hour() >= startTime
      ? userLocalTime.hour() + 1
      : startTime;

    let pickupSlot = roundTimeWithDayjs(
      dayjs.tz(
        `${date}T${userLocalTime.format('HH:mm:ss')}`,
        'YYYY-MM-DDTHH:mm:ss',
        'America/New_York',
      ),
      60,
    );

    pickupSlot = pickupSlot.set('hour', time);

    let readyTimestamp = pickupSlot;
    let companyCloseTime = pickupSlot.add(accessTime, 'seconds');

    const slotsForDay: Slot[] = [];

    while (companyCloseTime.hour() <= cutOffTime) {
      if (
        companyCloseTime.isAfter(maxSchedulePickupTime)
        && slotsForDay.length > 0
        && !readyTimestamp.isSame(dayjs(slotsForDay[slotsForDay.length - 1].readyTimestamp), 'day')
      ) {
        break;
      }

      if (companyCloseTime.hour() === cutOffTime && companyCloseTime.minute() < leftMinutes) {
        companyCloseTime = companyCloseTime.set('minute', leftMinutes);
      }

      if (
        ignoreLocalTime
        || (readyTimestamp.isAfter(userLocalTime)
          && companyCloseTime.isAfter(userLocalTime.add(2, 'hours')))
      ) {
        const slot: Slot = {
          dayText: readyTimestamp.format('ddd'), // Abbreviated day, e.g., "Mon"
          readyTimestamp: readyTimestamp.format('YYYY-MM-DDTHH:mm:ss'),
          timeText: `${readyTimestamp.format('h:mma')} - ${companyCloseTime.format('h:mma')}`,
          companyCloseTime: companyCloseTime.format('HH:mm:ss'),
          earlySlot: readyTimestamp.hour() < FEDEX_OFFICIAL_START_TIME,
        };
        slotsForDay.push(slot);
      }

      const futureTime = companyCloseTime.add(accessTime, 'seconds');
      if (companyCloseTime.hour() < cutOffTime && futureTime.hour() > cutOffTime) {
        companyCloseTime = companyCloseTime.set('hour', cutOffTime);
        readyTimestamp = companyCloseTime.subtract(accessTime, 'seconds');
      } else {
        readyTimestamp = companyCloseTime;
        companyCloseTime = futureTime;
      }
    }

    if (slotsForDay.length > 0) {
      groupedSlotsByDay.push({
        dayText: readyTimestamp.format('ddd'), // "Mon", "Tue", etc.
        dayNum: readyTimestamp.format('D'),
        monthText: readyTimestamp.format('MMM'), // "Sep", "Oct", etc.
        date,
        slots: slotsForDay,
      });
    }
  });

  return groupedSlotsByDay;
};

function PickupTimeContent({ nextStep }: { nextStep: () => void }) {
  const dispatch = useAppDispatch();
  const itemPublicId = useAppSelector(selectItemPublicId);
  const stateSlot = useAppSelector(getSlot);
  const pickupAvailability = useAppSelector(getPickupAvailability);
  const userLocalTime = useAppSelector(getUserLocalTime);
  const scheduleShippingLoading = useAppSelector(getScheduleShippingLoading);
  const days = buildSlots(pickupAvailability || [], userLocalTime || '');
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(stateSlot || null);
  const [selectedDate, setSelectedDate] = useState<Day['date'] | null>(selectedSlot ? selectedSlot.readyTimestamp.split('T')[0] : null);
  const [showErrorState, setShowErrorState] = useState(false);
  const onClickDay = (day: Day, slot: Slot) => {
    setSelectedDate(day.date);
    setSelectedSlot(slot);
  };

  const handleSubmit = async () => {
    if (selectedSlot) {
      dispatch(setSlot(selectedSlot));
      WorthyAPI.logUserAction(
        `User scheduled early pickup. readyTimestamp: ${selectedSlot?.readyTimestamp} companyCloseTime: ${selectedSlot?.companyCloseTime}`,
        itemPublicId,
      ).catch((err) => {
        console.error(
          `Error logging User scheduled early pickup. readyTimestamp: ${selectedSlot?.readyTimestamp} companyCloseTime: ${selectedSlot?.companyCloseTime} user action for item ${itemPublicId}`,
          err,
        );
      });
      const res = await dispatch(scheduleShipping());
      if (res.meta.requestStatus === 'fulfilled') {
        nextStep();
      } else {
        setShowErrorState(true);
      }
    }
  };

  const handleRetry = () => {
    setShowErrorState(false);
  };

  const handleDropOffInstead = () => {
    setShowErrorState(false);
    dispatch(setShippingMethod(SchedulingType.DROPOFF));
    WorthyAPI.logUserAction('Drop-off item instead button clicked', itemPublicId).catch((err) => {
      console.error(
        `Error logging Drop-off item instead button clicked user action for item ${itemPublicId}`,
        err,
      );
    });
    nextStep();
  };

  useEffect(() => {
    if (days.length > 0) {
      WorthyAPI.logUserAction(
        `Pickup time slots shown to the seller ${days.length}: ${days
          .map((day) => `${day.dayText} ${day.slots.map((slot) => slot.timeText).join(', ')}`)
          .join(', ')}`,
        itemPublicId,
      ).catch((err) => {
        console.error(
          `Error logging Pickup time slots shown to the seller user action for item ${itemPublicId}`,
          err,
        );
      });
    }
  }, [itemPublicId, days]);

  if (days.length === 0) {
    return (
      <ErrorState
        message="Currently there is no available slots for pickup in the next days, you can try again in a few days"
        buttonText="Drop-off item instead"
        onButtonClick={handleDropOffInstead}
      />
    );
  }

  if (showErrorState) {
    return (
      <ErrorState
        message="There seems to be an issue scheduling a pickup at the provided address."
        buttonText="Drop-off item instead"
        onRetry={handleRetry}
        onButtonClick={handleDropOffInstead}
      />
    );
  }

  return (
    <Stack gap={4}>
      <Box width="100%">
        <ScrollShadowContainer direction="horizontal" shadowColor="white" shadowSize={86}>
          <div style={{ display: 'flex', gap: 8, minWidth: 'max-content' }}>
            {days.map((day) => (
              <Stack key={day.date} gap={2} minWidth={190} textAlign="center" alignItems="center">
                <StyledChip
                  label={`${day.dayNum} ${day.monthText} ${day.dayText}`}
                  selected={selectedDate === day.date}
                />
                <Stack gap={1} width="100%" maxHeight={350} overflow="auto" p="2px">
                  {day.slots?.map((time) => (
                    <StyledPaper
                      key={time.timeText}
                      elevation={0}
                      onClick={() => onClickDay(day, time)}
                      selected={
                        selectedDate === day.date && selectedSlot?.timeText === time.timeText
                      }
                    >
                      <Typography key={time.timeText} variant="body1" color="inherit">
                        {time.timeText}
                      </Typography>
                    </StyledPaper>
                  ))}
                </Stack>
              </Stack>
            ))}
          </div>
        </ScrollShadowContainer>
      </Box>
      <Stack gap={1} alignItems="center">
        <NextButton
          size="large"
          onClick={handleSubmit}
          disabled={!selectedSlot}
          loading={scheduleShippingLoading}
          sx={{
            minWidth: { xs: 'auto', sm: 200 },
          }}
        />
      </Stack>
    </Stack>
  );
}

export default PickupTimeContent;
